import * as React from "react";
import { useEffect, useState } from "react";
import { busService } from "@modules/bus";
import { modelPool } from "@modules/model";
import { usersService } from "@modules/multiplayer/UsersService";
import "./MessageTab.component.css";
import { MessageRateChart } from "./MessageRateChart";
const MAX_MESSAGES = 50;
const MAX_DEPTH = 3;
const NUMBER_DECIMALS = 3;
const BASE_MESSAGE_KEYS = new Set([
    "type",
    "isBulkMessage",
    "isAskMessage"
]);
export function MessageTab() {
    const [messages, setMessages] = useState([]);
    const [filter, setFilter] = useState("");
    const [autoUpdate, setAutoUpdate] = useState(true);
    const [capturing, setCapturing] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState(null);
    /* initial populate */
    useEffect(() => {
        setMessages([]);
        setCapturing(true);
    }, []);
    const messageMatchesFilter = (m) => {
        if (filter === "")
            return true;
        const advancedTerms = parseAdvancedFilter(filter);
        if (advancedTerms !== null) {
            return advancedTerms.every(([key, value]) => matchesAdvancedFilterTerm(m, key, value));
        }
        const normalizedFilter = filter.toLowerCase();
        const simpleSearchText = [
            m.type,
            formatClientId(m.clientId),
            m.transport,
            m.originId,
            m.moduleId,
            formatTime(m.timestamp),
        ].join(" ").toLowerCase();
        if (simpleSearchText.includes(normalizedFilter)) {
            return true;
        }
        return messageContainsLeafValue(m.message, normalizedFilter);
    };
    // React to incoming messages
    useEffect(() => {
        const connection = busService.connection("MessageTab");
        const handler = connection.subscribe("*", (message, meta) => {
            if (meta.source.moduleId === "MessageTab")
                return;
            if (!autoUpdate && !capturing)
                return;
            const entry = {
                type: message.type,
                originId: meta.source.workerId,
                moduleId: meta.source.moduleId,
                clientId: meta.clientId,
                transport: meta.transport,
                timestamp: meta.timeStamp,
                message
            };
            setMessages((prev) => {
                const next = [entry, ...prev];
                if (next.length <= MAX_MESSAGES) {
                    return next;
                }
                // Prefer removing oldest non-matching entry
                const lastNonMatchingIndex = [...next]
                    .reverse()
                    .findIndex((m) => !messageMatchesFilter(m));
                if (lastNonMatchingIndex !== -1) {
                    const indexFromStart = next.length - 1 - lastNonMatchingIndex;
                    next.splice(indexFromStart, 1);
                }
                else {
                    // All match → remove oldest
                    next.pop();
                }
                if (!autoUpdate && next.length === MAX_MESSAGES) {
                    setCapturing(false);
                }
                return next;
            });
        });
        return () => {
            connection.unsubscribe(handler);
        };
    }, [autoUpdate, capturing, filter]);
    // Update when filter changes
    useEffect(() => {
        if (!autoUpdate)
            return;
        setMessages((prev) => {
            const next = [...prev];
            while (next.length > MAX_MESSAGES) {
                const lastNonMatchingIndex = [...next]
                    .reverse()
                    .findIndex((m) => !messageMatchesFilter(m));
                if (lastNonMatchingIndex !== -1) {
                    const indexFromStart = next.length - 1 - lastNonMatchingIndex;
                    next.splice(indexFromStart, 1);
                }
                else {
                    next.pop();
                }
            }
            return next;
        });
    }, [filter, autoUpdate]);
    const startManualGrab = () => {
        setMessages([]);
        setExpandedIndex(null);
        setCapturing(true);
    };
    const clearMessages = () => {
        setMessages([]);
        setExpandedIndex(null);
    };
    const filteredMessages = messages.filter(messageMatchesFilter);
    return (React.createElement("div", { className: "message-tab-container" },
        React.createElement("div", { className: "message-tab-header" },
            React.createElement("input", { type: "text", placeholder: "Filter messages\u2026", value: filter, onChange: (e) => {
                    setFilter(e.target.value);
                } }),
            React.createElement("button", { onClick: clearMessages }, "Clear"),
            React.createElement("button", { onClick: startManualGrab, disabled: autoUpdate || capturing }, "Update"),
            React.createElement("label", { className: "message-tab-checkbox" },
                React.createElement("input", { type: "checkbox", checked: autoUpdate, onChange: (e) => {
                        setAutoUpdate(e.target.checked);
                        if (e.target.checked)
                            setCapturing(false);
                    } }),
                "Auto update")),
        React.createElement("div", { className: "message-tab-table-wrapper" },
            React.createElement("table", { className: "message-tab-table" },
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", null, "Type"),
                        React.createElement("th", null, "Player"),
                        React.createElement("th", null, "Transport"),
                        React.createElement("th", null, "Origin"),
                        React.createElement("th", null, "Sender"),
                        React.createElement("th", null, "Time"))),
                React.createElement("tbody", null, filteredMessages.map((msg, i) => {
                    const expanded = expandedIndex === i;
                    const props = extractMessageProperties(msg.message);
                    return (React.createElement(React.Fragment, { key: i },
                        React.createElement("tr", { className: "message-row", onClick: () => {
                                setExpandedIndex(expanded ? null : i);
                            } },
                            React.createElement("td", null, msg.type),
                            React.createElement("td", null, formatClientId(msg.clientId)),
                            React.createElement("td", null, msg.transport),
                            React.createElement("td", null, msg.originId),
                            React.createElement("td", null, msg.moduleId),
                            React.createElement("td", null, formatTime(msg.timestamp))),
                        expanded && (React.createElement("tr", { className: "message-details-row" },
                            React.createElement("td", { colSpan: 6 }, props.length === 0 ? (React.createElement("div", { className: "message-details-empty" }, "No message-specific properties")) : (React.createElement("table", { className: "message-details-table" },
                                React.createElement("tbody", null, props.map(([key, value]) => (React.createElement("tr", { key: key },
                                    React.createElement("td", { className: "message-details-key" }, key),
                                    React.createElement("td", { className: "message-details-value" }, renderValue(value, 0)))))))))))));
                })))),
        React.createElement("div", { style: { marginTop: "8px" } },
            React.createElement(MessageRateChart, { enabled: autoUpdate || capturing }))));
}
/* ---------------- helpers ---------------- */
function extractMessageProperties(message) {
    return Object.keys(message)
        .filter((key) => !BASE_MESSAGE_KEYS.has(key))
        .map((key) => [key, message[key]]);
}
function parseAdvancedFilter(filter) {
    const trimmedFilter = filter.trim();
    if (trimmedFilter === "") {
        return null;
    }
    const parts = trimmedFilter.split(/\s+/);
    if (parts.length === 0 || !parts.every((part) => part.includes("="))) {
        return null;
    }
    const entries = [];
    for (const part of parts) {
        const separatorIndex = part.indexOf("=");
        if (separatorIndex <= 0) {
            return null;
        }
        const key = part.slice(0, separatorIndex).trim().toLowerCase();
        const value = part.slice(separatorIndex + 1).trim().toLowerCase();
        if (value === "") {
            return null;
        }
        entries.push([key, value]);
    }
    return entries;
}
function matchesAdvancedFilterTerm(message, key, value) {
    switch (key) {
        case "type":
            return message.type.toLowerCase().includes(value);
        case "player":
            return formatClientId(message.clientId).toLowerCase().includes(value);
        case "transport":
            return message.transport.toLowerCase().includes(value);
        case "origin":
            return message.originId.toLowerCase().includes(value);
        case "sender":
            return message.moduleId.toLowerCase().includes(value);
        case "time":
            return formatTime(message.timestamp).toLowerCase().includes(value) || String(message.timestamp).includes(value);
        case "prop":
            return messageContainsLeafValue(message.message, value);
        default:
            return false;
    }
}
function messageContainsLeafValue(value, filter, depth = 0) {
    if (value === null || value === undefined) {
        return String(value).toLowerCase().includes(filter);
    }
    if (depth >= MAX_DEPTH) {
        return false;
    }
    switch (typeof value) {
        case "string":
        case "boolean":
            return String(value).toLowerCase().includes(filter);
        case "number": {
            const formattedNumber = formatNumberOrUid(value).toLowerCase();
            return formattedNumber.includes(filter);
        }
        case "function":
            return false;
        case "object":
            if (Array.isArray(value)) {
                return value.some((item) => messageContainsLeafValue(item, filter, depth + 1));
            }
            return Object.entries(value).some(([key, childValue]) => {
                if (key.toLowerCase().includes(filter)) {
                    return true;
                }
                return messageContainsLeafValue(childValue, filter, depth + 1);
            });
        default:
            return String(value).toLowerCase().includes(filter);
    }
}
/* recursive renderer */
function renderValue(value, depth) {
    if (value === null)
        return React.createElement("em", null, "null");
    if (value === undefined)
        return React.createElement("em", null, "undefined");
    if (depth >= MAX_DEPTH)
        return React.createElement("em", null, "Max depth");
    switch (typeof value) {
        case "number":
            return formatNumberOrUid(value);
        case "string":
        case "boolean":
            return String(value);
        case "function":
            return React.createElement("em", null, "Function");
        case "object":
            if (Array.isArray(value)) {
                if (value.length <= 16 && value.every(v => typeof v === "number"))
                    return (value.length === 16) ? (React.createElement("table", { className: "message-subtable" },
                        React.createElement("tbody", null, [0, 1, 2, 3].map((i) => (React.createElement("tr", { key: i }, [0, 1, 2, 3].map((j) => (React.createElement("td", { className: "message-subtable-key matrix-element", key: j }, formatNumber(value[i * 4 + j])))))))))) : React.createElement("span", null, value.map(formatNumber).join(", "));
                return (React.createElement("span", null,
                    "Array(",
                    value.length,
                    ")"));
            }
            return renderObject(value, depth + 1);
        default:
            return String(value);
    }
}
function renderObject(obj, depth) {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
        return React.createElement("em", null);
    }
    return (React.createElement("table", { className: "message-subtable" },
        React.createElement("tbody", null, keys.map((key) => (React.createElement("tr", { key: key },
            React.createElement("td", { className: "message-subtable-key" }, key),
            React.createElement("td", { className: "message-subtable-value" }, renderValue(obj[key], depth))))))));
}
function formatNumber(v) {
    if (typeof v === "number")
        return v.toFixed(NUMBER_DECIMALS);
    return `NaN`;
}
function formatNumberOrUid(value) {
    if (Number.isInteger(value)) {
        const model = modelPool.getModel(value);
        if (model !== undefined) {
            return `${String(value)} (${String(model.getName())})`;
        }
    }
    return formatNumber(value);
}
function formatClientId(clientId) {
    if (clientId === undefined || clientId === "") {
        return "-";
    }
    const user = usersService.getUserByClientId(clientId);
    if (user !== undefined) {
        if (clientId === usersService.getLocalUser().clientId) {
            return "Me";
        }
        return `Player ${String(user.clientIdx)}`;
    }
    return clientId;
}
function formatTime(ts) {
    return new Date(ts).toLocaleTimeString();
}
